import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../../../../Constant';
import { getToken, getAuthHeaders, hasAuthSession } from '../../../../../utils/authStorage';
import { GlobalLoading, showLoading } from 'react-global-loading';
import { toast, ToastContainer } from 'react-toastify';
import messages from '../../../../../assets/Messages.json';
import 'react-toastify/dist/ReactToastify.css';
import { generateYearRange } from '../../../../../Custom/Custom';
import asiTitle from '../../../../../asi.json';
import BlockHJsonData from '../../../../../assets/BlockHData.json';

// API configuration
const getAuthConfig = () => {
  if (!hasAuthSession()) return null;
  return { ...getAuthHeaders(), headers: { ...getAuthHeaders().headers, 'Cache-Control': 'no-cache' } };
};

const BlockHReport = () => {
  const navigate = useNavigate();

  // State for report data
  const [itemData, setItemData] = useState({});
  const [blockHData, setBlockHData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Memoized block info from localStorage
  const blockInfo = useMemo(() => {
    const balanceSheetData = localStorage.getItem('balanceSheetFormData');
    if (!balanceSheetData) return {};

    const parsedData = JSON.parse(balanceSheetData);
    return {
      dslNo: parsedData.dslNo || '',
      surveyYear: parsedData.year || '2022-23',
      nameUnit: parsedData.nameUnit || '',
      pslNo: parsedData.pslNo || ''
    };
  }, []);

  const storedLanguage = localStorage.getItem('language') || 'en';
  const itemCodeArray = ['9920100', '9990100', '9920300', '9990800', '9990400'
    , '9990500', '9990600', '9990700', '9990900', '9920400', '9922000', '9992000', '9993000',
    '9999900'];

  // Memoized data transformation
  const transformBlockHData = useCallback((data) => {
    const initialData = {};
    data.forEach((item, index) => {
      initialData[index] = {
        id: index,
        dslNo: blockInfo.dslNo,
        surveyYear: blockInfo.surveyYear,
        itemDesc: item.itemDesc,
        itemCode: item.itemCode,
        unitOfQty: item.unitOfQty,
        qtyConsumed: item.qtyConsumed,
        purchaseValue: item.purchaseValue,
        ratePerUnit: item.ratePerUnit,
        statusFlag: item.statusFlag,
        saveOrderBy: item.saveOrderBy,
        itemCodeClass: 'textBox_back_color_white'
      };
    });
    return initialData;
  }, [blockInfo.dslNo, blockInfo.surveyYear]);

  // Function to add empty rows
  const addNineEmptyRow = useCallback((responseBlockHData, dataType, emptyRowCount) => {
    let customObject = [];
    let mergedArray = '';
    for (let i = 0; i <= emptyRowCount; i++) {
      const newItemAdded = {
        "dslNo": blockInfo.dslNo,
        "surveyYear": blockInfo.surveyYear,
        "itemDesc": "",
        "itemCode": "",
        "unitOfQty": "",
        "qtyConsumed": "",
        "purchaseValue": "",
        "ratePerUnit": "",
        "statusFlag": "",
        "saveOrderBy": 1,
        "itemCodeClass": "textBox_back_color_white",
      };
      customObject.push(newItemAdded);
    }

    if (dataType === "dynamic") {
      // Find the index where static items (itemCodeArray items) start
      // After sorting, static items (itemCodeArray) come after user entries
      const dataArray = Object.values(responseBlockHData);
      let AddedIndex = dataArray.length;
      
      // Find first occurrence of any static itemCode (itemCodeArray)
      // Since data is already sorted, first static item marks the start of static items
      for (let i = 0; i < dataArray.length; i++) {
        if (itemCodeArray.includes(dataArray[i].itemCode)) {
          AddedIndex = i;
          break;
        }
      }
      
      // Ensure we don't create duplicates - only insert blank rows before static items
      // If all items are static, insert at the beginning
      if (AddedIndex === dataArray.length) {
        AddedIndex = 0;
      }
      
      mergedArray = [
        ...responseBlockHData.slice(0, AddedIndex),
        ...customObject,
        ...responseBlockHData.slice(AddedIndex)
      ];
    } else if (dataType === "static") {
      mergedArray = [
        ...responseBlockHData.slice(0, 0),
        ...customObject,
        ...responseBlockHData.slice(0)
      ];
    }

    return mergedArray;
  }, [blockInfo.dslNo, blockInfo.surveyYear]);

  // Fetch data function
  const fetchBlockHData = useCallback(async () => {
    if (!blockInfo.dslNo || !blockInfo.surveyYear) {
      console.log('Missing DSL No or Survey Year');
      return;
    }

    const requestPayload = {
      dsl_no: blockInfo.dslNo,
      survey_year: blockInfo.surveyYear.split('-')[0]
    };

    showLoading(true);

    try {
      const config = getAuthConfig();
      if (!config) {
        toast.error('Authentication token not found. Please login again.', messages.COLOR_RED);
        return;
      }

      const response = await axios.post(`${API_URL}api/asi/blockHForm`, requestPayload, config);
      const { data, status } = response.data;

      let responseBlockHData = '';

      if (status && data?.blockHData?.length > 0) {
        data.blockHData.sort((a, b) => {
          const indexA = itemCodeArray.indexOf(a.itemCode);
          const indexB = itemCodeArray.indexOf(b.itemCode);
          return indexA - indexB;
        });
        let numofblankrow = 24 - data.blockHData.length;
        responseBlockHData = addNineEmptyRow(data.blockHData, 'dynamic', numofblankrow - 1);
      } else {
        responseBlockHData = addNineEmptyRow(BlockHJsonData, 'static', 9);
      }

      setBlockHData(responseBlockHData);
      const transformedData = transformBlockHData(responseBlockHData);
      setItemData(transformedData);
      console.log('Block H data loaded successfully:', responseBlockHData.length, 'items');
    } catch (error) {
      console.error('API Error:', error);

      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.', messages.COLOR_RED);
      } else if (error.response?.status === 404) {
        toast.warning('No data found for this DSL No. and Survey Year.', messages.COLOR_RED);
      } else {
        toast.error(`Error loading data: ${error.message}`, messages.COLOR_RED);
      }
      setBlockHData([]);
    } finally {
      showLoading(false);
    }
  }, [blockInfo.dslNo, blockInfo.surveyYear, transformBlockHData, addNineEmptyRow]);

  useEffect(() => {
    fetchBlockHData();
  }, [fetchBlockHData]);

  // Memoized table headers
  const tableHeaders = useMemo(() => {
    const isHindi = storedLanguage === 'hi';
    return {
      slNo: isHindi ? asiTitle.hi.SL_NO : asiTitle.en.SL_NO,
      itemDescription: isHindi ? asiTitle.hi.ITEM_dESCRIPTION : asiTitle.en.ITEM_dESCRIPTION,
      itemCode: isHindi ? asiTitle.hi.ITEM_CODE : asiTitle.en.ITEM_CODE,
      unitOfQuantity: isHindi ? asiTitle.hi.UNIT_OF_QUANTITY : asiTitle.en.UNIT_OF_QUANTITY,
      quantityConsumed: isHindi ? asiTitle.hi.QUANTITY_CONSUMED : asiTitle.en.QUANTITY_CONSUMED,
      purchaseValue: isHindi ? asiTitle.hi.PURCHASE_VALUE : asiTitle.en.PURCHASE_VALUE,
      ratePerUnit: isHindi ? asiTitle.hi.RATE_PER_UNIT : asiTitle.en.RATE_PER_UNIT,
      dslNo: isHindi ? asiTitle.hi.DSL_NO : asiTitle.en.DSL_NO,
      surveyYear: isHindi ? asiTitle.hi.SERVEY_YEAR : asiTitle.en.SERVEY_YEAR,
      blockHTitle: isHindi ? asiTitle.hi.BLOCK_H_TITLE : asiTitle.en.BLOCK_H_TITLE
    };
  }, [storedLanguage]);

  return (
    <>
      <GlobalLoading />
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover theme="light" />

      <style>
        {`
          .report-container {
            background-color: #fff;
            border: 1px solid #ddd;
            border-radius: 6px;
            padding: 20px;
            margin-bottom: 20px;
          }
          .report-title {
            background-color: #fff;
            color: #333;
            font-size: 18px;
            font-weight: bold;
            padding: 15px 20px;
            margin: -20px -20px 20px -20px;
            border-radius: 6px 6px 0 0;
            border-bottom: 2px solid #333;
            text-align: center;
          }
          .report-info {
            background-color: #f8f9fa;
            padding: 10px 15px;
            margin-bottom: 20px;
            border-radius: 4px;
            border-left: 4px solid #333;
          }
          .report-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 20px;
            font-size: 11px;
          }
          .report-table th,
          .report-table td {
            border: 1px solid #333;
            padding: 4px 2px;
            text-align: center;
            vertical-align: middle;
          }
          .report-table th {
            background-color: #f5f5f5;
            color: #333;
            font-weight: bold;
            font-size: 10px;
          }
          .report-table td {
            background-color: #fff;
          }
          .report-table .item-name {
            text-align: left;
            font-weight: 500;
            padding-left: 4px;
          }
          .report-table .bold-text {
            font-weight: bold;
          }
          .no-data {
            text-align: center;
            color: #666;
            font-style: italic;
            padding: 40px;
            background-color: #f8f9fa;
            border-radius: 4px;
          }
         
        
          @media print {
            .no-print { display: none !important; }
            .report-container { border: none; margin: 0; padding: 0; }
            .report-title { margin: 0; border-radius: 0; }
            .report-info { margin: 0 0 20px 0; }
            .btn_orange { display: none !important; }
          }
        `}
      </style>

      <div className="container-fluid py-3">
        {/* Report Content */}
        <div className="report-container">
          <div className="report-title">
            <div dangerouslySetInnerHTML={{ __html: tableHeaders.blockHTitle }} />
          </div>

          <div className="report-info">
            <div className="row">
              <div className="col-md-6">
                <strong>{tableHeaders.dslNo}:</strong> {blockInfo.dslNo || 'Not Available'}
              </div>
              <div className="col-md-6">
                <strong>{tableHeaders.surveyYear}:</strong> {blockInfo.surveyYear || 'Not Available'}
              </div>
            </div>
          </div>

          {blockHData.length > 0 ? (
            <div className="table-responsive">
              <table className="report-table">
                <thead>
                  <tr>
                    <th style={{ width: '8%' }}>{tableHeaders.slNo}</th>
                    <th style={{ width: '25%' }}>{tableHeaders.itemDescription}</th>
                    <th style={{ width: '12%' }}>{tableHeaders.itemCode}</th>
                    <th style={{ width: '12%' }}>{tableHeaders.unitOfQuantity}</th>
                    <th style={{ width: '15%' }}>{tableHeaders.quantityConsumed}</th>
                    <th style={{ width: '15%' }}>{tableHeaders.purchaseValue}</th>
                    <th style={{ width: '13%' }}>{tableHeaders.ratePerUnit}</th>
                  </tr>
                  <tr>
                    <th>1</th>
                    <th>2</th>
                    <th>3</th>
                    <th>4</th>
                    <th>5</th>
                    <th>6</th>
                    <th>7</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.values(itemData).map((item, i) => {
                    // Continuous serial numbering: 1, 2, 3, ..., 10, 11, 12, ... (no duplicates)
                    let serialNumber = i + 1;

                    const isBold = ['9990100', '9992000', '9993000'].includes(item.itemCode);
                    const isKWH = ['9990400', '9990500', '9999900'].includes(item.itemCode);

                    return (
                      <tr key={i}>
                        <td>{serialNumber}</td>
                        <td className={`item-name ${isBold ? "bold-text" : ""}`}>
                          {item.itemDesc}
                        </td>
                        <td>{item.itemCode}</td>
                        <td>
                          {isKWH ? "KWH" : item.unitOfQty}
                        </td>
                        <td>{item.qtyConsumed === "0" ? "" : item.qtyConsumed || ''}</td>
                        <td>{item.purchaseValue === "0" ? "" : item.purchaseValue || ''}</td>
                        <td>{item.ratePerUnit === "0" ? "" : item.ratePerUnit || ''}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="no-data">No Block H data available for this DSL No.</div>
          )}
        </div>

        {/* Action Buttons */}
        {/* <div className="row no-print">
          <div className="col-12">
            <hr />
          </div>
          <div className="col-md-8 text-center text-md-start mb-3 mb-md-0">
            <button 
              type="button" 
              className="btn btn_orange mx-1"
              onClick={() => window.print()}
            >
              Print  
              <i className="fa fa-print ms-1"></i>
            </button>
            <button 
              type="button" 
              className="btn btn_orange mx-1"
              onClick={() => navigate('/asiScheduleMainMenu')}
            >
              Cancel
            </button>
          </div>
          <div className="col-md-4 text-center text-md-end">
            <button 
              type="button" 
              className="btn btn_orange mx-1"
              onClick={() => navigate(-1)}
            >
              <i className="fa fa-angle-left me-1"></i>
              Previous
            </button>
            <button 
              type="button" 
              className="btn btn_orange mx-1"
              onClick={() => navigate('/reports')}
            >
              Next
              <i className="fa fa-angle-right ms-1"></i>
            </button>
          </div>
        </div> */}

        <div className="row no-print">
          <div className="col-12">
            <hr />
          </div>
          <div className="col-md-8 text-center text-md-start mb-3 mb-md-0">
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={() => window.print()}
            >
              Print
              <i className="fa fa-print ms-1"></i>
            </button>
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={() => navigate('/report-selection')}
            >
              Cancel
            </button>
          </div>
          <div className="col-md-4 text-center text-md-end">
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={() => navigate(-1)}
            >
              <i className="fa fa-angle-left me-1"></i>
              Previous
            </button>
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={() => navigate('/blockIreport')}
            >
              Next
              <i className="fa fa-angle-right ms-1"></i>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default BlockHReport;